/**
 * Customized to pass new param to set a new UI design
 */

import { B2B } from '@/components/blocks/B2B';
import {
	addToWishListCartViewSX,
	addToWishListSX,
	wishListMenuPaperCartView,
	wishListMenuPaperSX,
} from '@/components/blocks/ProductDetails/styles/addToWishList';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useSkuListTable } from '@/data/Content/SkuListTable';
import { useWishRequisitionList } from '@/data/Content/_WishRequisitionList';
import { useLocalization } from '@/data/Localization';
import { ADD_TO_LISTS_DISPLAY_TIMEOUT, TYPES } from '@/data/constants/product';
import { ContentContext } from '@/data/context/content';
import { mergeSx } from '@/utils/mergeSx';
import { productIsA } from '@/utils/productIsA';
import { Add, Check, Close } from '@mui/icons-material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import {
	Box,
	Button,
	IconButton,
	Menu,
	MenuItem,
	PopoverOrigin,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { kebabCase } from 'lodash';
import { useTrackAddToWishList } from 'presentation/events/hooks/useTrackAddToWishList';
import { FC, MouseEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useGetCategories } from 'shared/custom/hooks/Categories';
import { findParentCategories } from 'shared/custom/utils/getCategoryByIdForGA';

type Props = {
	standalone?: boolean;
	isIconButton?: boolean;
	isSkuInWishList?: boolean;
	isStickyComponent?: boolean;
	isInCartPage?: boolean;
};

export const ProductDetailsAddToWishList: FC<Props> = ({
	standalone = false,
	isSkuInWishList = false,
	isStickyComponent,
	isInCartPage = false,
}) => {
	const [anchor, setAnchor] = useState<null | HTMLElement>(null);
	const onOpen = (event: React.MouseEvent<HTMLButtonElement>) => setAnchor(event?.currentTarget);
	const onClose = () => setAnchor(null);
	const [isLoading, setIsLoading] = useState(false);
	const productDetailText = useLocalization('productDetail');
	const RequisitionListsText = useLocalization('RequisitionLists');
	const { CartOrderAddList } = useLocalization('HDMProductDetail');

	const WISHLIST_MENU: Record<string, PopoverOrigin> = {
		anchorOrigin: { vertical: 'bottom', horizontal: isInCartPage ? 'center' : 'right' },
		transformOrigin: { vertical: 'top', horizontal: isInCartPage ? 'center' : 'right' },
	};

	const trackAddToWishList = useTrackAddToWishList();

	const { selection, loginStatus, addToWishList, product, addToNewWishlist } = useContext(
		ContentContext
	) as ReturnType<typeof useProductDetails> & ReturnType<typeof useSkuListTable>;
	const {
		wishLists,
		mutateWishList, // This is the most common name for a refresh function in these hooks
	} = useWishRequisitionList();

	const quantityValue = product?.['x_measurements.nominalQuantity'] || 1;

	const isBundle = productIsA(product, TYPES.bundle);
	const sku = isInCartPage ? product : !isBundle ? selection?.sku : product;
	const quantity = isInCartPage ? +quantityValue : !isBundle ? selection?.quantity : 1;
	const [isInWishList, setIsInWishList] = useState<boolean>(false);
	const dtId = useMemo(() => `wishlist-selection${standalone ? '-standalone' : ''}`, [standalone]);
	const [isCreating, setIsCreating] = useState(false);
	const [newListName, setNewListName] = useState('');
	const { navigation } = useGetCategories();

	const handleCancel = (e: MouseEvent) => {
		e.stopPropagation(); // Prevent Menu from closing
		setIsCreating(false);
		setNewListName('');
	};

	const handleSubmit = async (e: MouseEvent) => {
		e.stopPropagation();
		const trimmedName = newListName.trim();
		if (!trimmedName) {
			return;
		}

		const nameExists = wishLists?.some(
			(wl) => wl.description?.toLowerCase() === trimmedName.toLowerCase()
		);

		if (nameExists) {
			return;
		}

		try {
			setIsLoading(true);
			await addToNewWishlist(sku?.partNumber ?? '', quantity, trimmedName)();

			setIsInWishList(true);

			if (mutateWishList) {
				await mutateWishList();
			}
			setIsCreating(false);
			setNewListName('');
			if (sku) {
				trackAddToWishList({
					product: sku,
					categories: findParentCategories(navigation, sku.parentCatalogGroupID) ?? [],
				});
			}
			onClose();
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	const onClick = useCallback(
		(partNumber = '', quantity: number, list: NonNullable<typeof wishLists>[number]) =>
			async (_event: MouseEvent<HTMLElement>) => {
				setTimeout(onClose, ADD_TO_LISTS_DISPLAY_TIMEOUT);
				await addToWishList(partNumber, quantity, list)();
				setIsInWishList(true);
				if (mutateWishList) await mutateWishList();
				trackAddToWishList({
					product: sku!,
					categories: findParentCategories(navigation, sku?.parentCatalogGroupID ?? '') ?? [],
					quantity,
				});
			},
		[addToWishList, mutateWishList]
	);

	useEffect(() => {
		const handleResize = () => {
			if (anchor && !isCreating) {
				onClose();
			}
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [anchor, isCreating, onClose]);

	return loginStatus ? (
		<B2B is={false}>
			<Button
				disableRipple={isInCartPage}
				data-testid={`${dtId}-default`}
				id={isStickyComponent ? 'stickyAddToList' : 'addToList'}
				sx={mergeSx(
					addToWishListSX.newDesignContainer,
					isInCartPage ? addToWishListCartViewSX.button : {}
				)}
				onClick={onOpen}
			>
				{isInCartPage ? (
					<>
						{isSkuInWishList || isInWishList ? null : (
							<Typography
								variant="bodyBaseline"
								color={'secondary.main'}
								sx={addToWishListCartViewSX.text}
							>
								{CartOrderAddList.t()}
							</Typography>
						)}
					</>
				) : (
					<>
						{isSkuInWishList || isInWishList ? (
							<FavoriteIcon sx={addToWishListSX.wishListFillIcon} />
						) : (
							<FavoriteBorderOutlinedIcon />
						)}
						<Typography sx={addToWishListSX.wishListLabelSX} variant="bodySubText">
							{productDetailText.wishList.t()}
						</Typography>
					</>
				)}
			</Button>

			<Menu
				anchorEl={anchor}
				open={!!anchor}
				onClose={onClose}
				anchorOrigin={WISHLIST_MENU.anchorOrigin}
				transformOrigin={WISHLIST_MENU.transformOrigin}
				slotProps={{ paper: isInCartPage ? wishListMenuPaperCartView : wishListMenuPaperSX }}
				disableRestoreFocus
				disableAutoFocus
				disableEnforceFocus
				MenuListProps={{
					autoFocusItem: false,
				}}
			>
				{(!wishLists || wishLists.length === 0) && (
					<MenuItem sx={addToWishListSX.emptyStateMenuItemSX}>
						<Typography variant="body2" color="textSecondary">
							{/* Check PDF for exact "Empty State" text */}
							{RequisitionListsText.EmptyList.t()}
						</Typography>
					</MenuItem>
				)}
				<Box sx={addToWishListSX.wishListItemBoxSX}>
					{wishLists?.map((wl, i) => (
						<MenuItem
							key={i}
							data-testid={kebabCase(`add-to-wishlist-options-${wl.description}-${i}-menu-item`)}
							id={kebabCase(`add-to-wishlist-options-${wl.description}-${i}-menu-item`)}
							value={wl.uniqueID}
							sx={addToWishListSX.wishListMenuItemSX}
							onClick={onClick(sku?.partNumber, quantity, wl)}
						>
							<Typography sx={addToWishListSX.wishListTypographySX}>{wl.description}</Typography>
						</MenuItem>
					))}
				</Box>
				<MenuItem sx={addToWishListSX.wishListMenuItemSX} disableRipple={isCreating}>
					{!isCreating ? (
						<Stack
							direction="row"
							alignItems="center"
							onClick={() => setIsCreating(true)}
							sx={mergeSx(
								addToWishListSX.wishListCreateLinkSX,
								isInCartPage ? addToWishListCartViewSX.color : {}
							)}
						>
							<Add fontSize="small" />
							<Typography sx={{ color: 'inherit', fontSize: 'inherit' }}>
								{RequisitionListsText.CreateList.t()}
							</Typography>
						</Stack>
					) : (
						<Stack
							direction="row"
							alignItems="center"
							spacing={1}
							onClick={(e) => e.stopPropagation()}
						>
							<TextField
								autoFocus
								size="small"
								variant="standard"
								value={newListName}
								onChange={(e) => setNewListName(e.target.value)}
								onKeyDown={(e) => {
									e.stopPropagation();
									if (e.key === 'Enter') handleSubmit(e as any);
									if (e.key === 'Escape') handleCancel(e as any);
								}}
								sx={addToWishListSX.wishListInputSX}
								InputProps={{ disableUnderline: true }}
								disabled={isLoading}
								onClick={(e) => e.stopPropagation()}
								onTouchStart={(e) => e.stopPropagation()}
								onMouseDown={(e) => e.stopPropagation()}
								onFocus={(e) => e.stopPropagation()}
							/>
							<IconButton
								size="small"
								onClick={handleSubmit}
								sx={addToWishListSX.wishListIconButtonSX}
								aria-label="Save"
							>
								<Check />
							</IconButton>
							<IconButton
								size="small"
								onClick={handleCancel}
								sx={addToWishListSX.wishListIconButtonSX}
								aria-label="Cancel"
							>
								<Close />
							</IconButton>
						</Stack>
					)}
				</MenuItem>
			</Menu>
		</B2B>
	) : null;
};
